/*************************************************************************\
Provides a client side interface for interacting with AuthAPI and
automatically dealing with certain un-auth'd conditions such as getting a
new auth-token and retrying requests.

This api.auth should generally NOT BE USED by user code, except by
the sign-in/out interface. Most code should just use the api calls.

See export at bottom of file for available public properties.
\*************************************************************************/

import { config } from '../app/config.js';
import { log } from '../app/log.js';
import { transfer } from './auth-transfer.js';

/* Private Classes *******************************************************/

const exceptions = {
    ArgumentsInvalidException: class {
        constructor(message) {
            this.message = message;
        }
    },
    UnexpectedException: class {
        constructor(message) {
            this.message = message;
        }
    },
    // Auth exceptions, don't use these with anything except appropriate auth stuff.
    UnauthorizedException: class {
        constructor(message) {
            this.message = message;
            window.dispatchEvent(new Event('api.auth.exceptions.UnauthorizedException')); // So general code can react (ie see userwidget).
        }
    },
    ForbiddenException: class {
        constructor(message) {
            this.message = message;
        }
    }
};

class FetchOptions {
    constructor(options) {
        // Defaults.
        this.method = 'get'; // Default method, can be overridden by options.
        this.mode = 'cors'; // CORS mode, meaning we can perform full cross-domain requests (and get responses) if server agrees.
        //this.credentials = 'include'; // Include cookies.
        this.cache = 'no-store'; // Don't cache.

        // Apply custom values (possibly adding values and/or overiding defaults).
        Object.assign(this, options);

        // Create headers object if it wasn't specified, or if not of Headers type then init create with specified data.
        if (!this.headers || !(this.headers instanceof Headers)) {
            this.headers = new Headers(this.headers || {});
        }

        if (!this.headers.has('Content-Type')) {
            // Add correct content-type header for api calls.
            // We used to specify 'text/plain' so as to avoid CORS pre-flight requests (OPTIONS),
            // however dotnet 3.1 introduced System.Text.Json which didn't support lots of stuff
            // so we had to use their fallback AddNewtonsoftJson feature, which in turn screwed
            // up our ability to accept 'text/plain' content-types... so now we're just using the
            // more normal 'application/json' content-type (which is maybe better?).
            this.headers.append('Content-Type', 'application/json');
        }
    }
}

/* Private Functions *****************************************************/

function clearToken() {
    localStorage.removeItem(config.storageKeys.authToken);
}
function getToken() {
    return localStorage.getItem(config.storageKeys.authToken);
}
function storeToken(data) {
    if (!data || !data.authToken) {
        // No data or no authToken then we should clear out any old token in storage,
        // since we should now assume it's invalid.
        clearToken();
        return false; // No valid token.
    }
    // Otherwise store token as provided...

    if (data.authToken) {
        localStorage.setItem(config.storageKeys.authToken, data.authToken);
        return true; // Auth-token stored and ready.
    }

    return false; // No token stored.
}

/*
Send a token to the AuthAPI using GET method to attempt to get a refreshed token,
the AuthAPI will check that the token is valid and the matching auth session is
valid on the server; if everything checks out then a new auth-token will be provided
which in-turn can be used to authenticate regular (non AuthAPI) API calls.

Returns true/false whether the auth-token was successfully refreshed,
or an exception if something failed (which would be unexpected).
*/
function refresh({ transferTokenCallback, params }) {
    const token = getToken();

    // No token so no point in refreshing, so just resolve with non-exceptional failure.
    if (!token) return Promise.resolve(false);

    params = !params ? '' : Object.keys(params)
        .filter(k => { const v = params[k]; return v !== null && v !== undefined; }) // Remove bad entries.
        .map(k => k + '=' + params[k] + '&'); // Map to URL param format.

    const url = config.endpoints.authAPI + '?' + params +
        // If a callback for transfer token is specified then request a transfer token.
        (transferTokenCallback ? 'createTransferToken=true' : '');

    // Otherwise we have a token so lets try to get a new/refreshed token...
    return fetch(url, new FetchOptions({
        method: 'get',
        headers: new Headers({ 'authorization': `Bearer ${token}` })
    }))
        .then(handleAuthResponse)
        .then(function (result) {
            // If transfer token callback exists then call it (there should be a transfer token in the data).
            if (result && transferTokenCallback)
                transferTokenCallback(result.data);

            return processAuthResult(result);
        })
        .catch(function (ex) {
            // This shouldn't happen, maybe some type of communication/network/server failure?
            log.critical('api.auth.refresh', ex);
            return Promise.reject(ex);
        });
}

/*
The server will check credentials and return a token if valid.

Returns true/false whether the sign-in was successful or not,
or an exception if something failed (which would be unexpected).
*/
function signIn(user, pass, isAdmin) {
    // Check arguments aren't stupid.
    if (!user || typeof user !== 'string' || user.length < 3 ||
        !pass || typeof pass !== 'string' || pass.length < 6) {
        return Promise.reject(new exceptions.ArgumentsInvalidException());
    }

    // Clear stored token.
    clearToken();

    const payload = {
        User: user,
        Pass: pass
    };

    if (isAdmin) {
        payload.IsAdmin = true;
    }
    else {
        payload.SiteId = config.siteId;
    }

    // Make the sign-in request.
    return fetch(config.endpoints.authAPI, new FetchOptions({
        method: 'post',
        body: JSON.stringify(payload)
    }))
    .then(handleAuthResponse)
    .then(processAuthResult)
    .catch(function (ex) {
        // This shouldn't happen, maybe some type of communication/network/server failure?
        log.critical('api.auth.signIn', ex);
        return Promise.reject(ex);
    });
}

/*
The server will check the transferToken (for the user) and if valid then the associated
auth-session will be used to create an auth token (like a normal sign-in).

Returns true/false whether the sign-in was successful or not,
or an exception if something failed (which would be unexpected).
*/
function signInTransferToken(userId, transferToken) {
    // Check arguments aren't stupid.
    if (!userId || typeof userId !== 'number' || userId <= 0 ||
        !transferToken || typeof transferToken !== 'string' || !transferToken.length) {
        return Promise.reject(new exceptions.ArgumentsInvalidException());
    }

    // Clear stored token.
    clearToken();

    const payload = {
        SiteId: config.siteId,
        UserId: userId,
        TransferToken: transferToken
    };

    // Make the sign-in request.
    return fetch(config.endpoints.authAPI, new FetchOptions({
        method: 'put',
        body: JSON.stringify(payload)
    }))
    .then(handleAuthResponse)
    .then(processAuthResult)
    .catch(function (ex) {
        // This shouldn't happen, maybe some type of communication/network/server failure?
        log.critical('api.auth.signInTransferToken', ex);
        return Promise.reject(ex);
    });
}

/*
The server will invalidate and clear the credentials.
*/
function signOut() {
    const token = getToken();

    // No token so no point in deleting, so just resolve with non-exceptional failure.
    if (!token) return Promise.resolve(false);

    // Clear token from storage first.
    clearToken();

    return fetch(config.endpoints.authAPI, new FetchOptions({
        method: 'delete',
        headers: new Headers({ 'Authorization': `Bearer ${token}` })
    }))
    .catch(function (ex) {
        // This shouldn't happen, maybe some type of communication/network/server failure?
        log.critical('api.auth.signOut', ex);
        Promise.reject(ex);
    });
}

function handleAuthResponse(response) {
    if (response.ok) {
        return response.json().then(json => {
            return Promise.resolve({
                ok: true,
                status: response.status,
                data: json
            });
        });
    }
    else if (response.status === 401) {
        // Credentials supplied were not good, auth failed.
        return response.text().then(text => {
            return Promise.resolve({
                ok: false,
                status: response.status,
                data: text || null
            });
        });
    }
    else {
        // This is unexpected, why are we getting some other response code?
        // Could get 400 BadRequest if no tokens sent, but that shouldn't ever occur.
        // If this is happening then it needs to be looked into and fixed.
        log.critical('api.auth.handleAuthResponse{unexpected-status}', response);
        return Promise.reject(new exceptions.UnexpectedException('auth-unexpected-response'));
    }
}
function processAuthResult(result) {
    // Handle resulting json (already decoded from response body),
    // should either be a successful response decoded into json, or falsy.

    if (result && result.ok) {
        // Store token, will clear existing token and replace if valid.
        storeToken(result.data);

        // Remove auth token from data, it is stored now and app shouldn't use it.
        // Leaving the remaining values for use by app.
        delete result.data.authToken;
    }
    // Else auth failed, just leave data as is (ie null or failure code).

    return result;
}

/* Public Interface ******************************************************/

export const auth = {
    exceptions: exceptions,

    addAuthorizationHeader(headers) {
        if (!headers) return false;

        let token = getToken();
        if (!token) return false;

        token = `Bearer ${token}`;

        if (headers instanceof Headers)
            headers.append('Authorization', token);
        else
            headers['Authorization'] = token;

        return true;
    },

    refresh: (params) => refresh({ params }),
    transferAdmin: () => transfer.toPhoenixAdmin((transferTokenCallback) => refresh({ transferTokenCallback })),

    signIn: (username, password) => signIn(username, password, /*isadmin:*/false),
    signInAdmin: (username, password) => signIn(username, password, /*isadmin:*/true),
    signInTransferToken: signInTransferToken,
    signOut: signOut
};

// TODO: Consider having a Shared Web Worker that refreshes the auth token on
// auth token expiry period intervals. This would maintain the sing-in state.
// Though it isn't probably necessary, since the auth refresh token lasts for
// a long time (24h) so even if the token expires we can still get a refresh
// for that period, and once we get a refresh the cookie will extend.